import clsx from "clsx";
import {
  FunctionComponent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import IntroCovers, {
  type IntroPhase,
} from "@story/data-centers-hub/components/IntroCovers";
import { introWipeMs } from "@story/data-centers-hub/config/intro";
import { scheduleEnterFillPins } from "@story/data-centers-hub/config/scheduleEnterFillPins";
import type { EditorNoteBlock } from "@story/data-centers-hub/config/editorNoteBlocks";
import type {
  DeckSlideId,
  GridCellPlacement,
} from "@story/data-centers-hub/config/slides";
import {
  asStill,
  titleSlideMedia,
} from "@story/data-centers-hub/config/titleAssets";
import { wipeClipAnimAttrs } from "@story/data-centers-hub/config/wipeClipAnimAttrs";
import { useHubMotionOn } from "@story/data-centers-hub/hooks/useHubMotionEnabled";
import { useHubNarrowViewport } from "@story/data-centers-hub/hooks/useHubNarrowViewport";
import { useStageBottomClearance } from "@story/data-centers-hub/hooks/useStageBottomClearance";
import getBrowserInfo from "src/utils/get-browser-info";

import TitleCellMedia from "./TitleCellMedia";
import * as styles from "./TitleSlide.css";

interface Props {
  /**
   * Desktop 3×2 placements — still used by IntroCovers for the note tiling.
   * The title mosaic itself is absolute CSS (Arizona pattern).
   */
  cells: ReadonlyArray<GridCellPlacement>;
  active: boolean;
  /** Editor's note blocks. Empty skips the black intro. */
  editorNoteBlocks?: ReadonlyArray<EditorNoteBlock>;
  introPhase?: IntroPhase;
  onIntroStart?: (landing?: DeckSlideId) => void;
  onIntroDone?: () => void;
  onIntroSettled?: () => void;
  /** In-note chapter link — cover wipe lands on that stop. */
  onSlideLink?: (id: DeckSlideId) => void;
  /** Scroller offset saved when the note last wiped away. */
  restoredScroll?: number;
  onNoteScroll?: (top: number) => void;
}

/** Stage-aligned mobile hed — wipe clone in a cell, or settled overlay. */
function HedBleed({
  offsetClass,
  active,
}: {
  offsetClass?: string;
  active: boolean;
}) {
  return (
    <div className={clsx(styles.hedBleed, offsetClass)} aria-hidden="true">
      <img
        className={styles.hedImg}
        src={titleSlideMedia.hedMobile}
        alt=""
        decoding="async"
        loading="eager"
        fetchPriority={active ? "high" : "low"}
      />
    </div>
  );
}

/**
 * Title mosaic — chapter-style absolute panels + hed.
 * Desktop: single stage hed (fromCenter). Narrow wipe: bleed clones inside
 * each panel. Narrow settled: one high-z `hedBleed` above cell rules.
 * IntroCovers owns the editor’s note.
 */
const TitleSlide: FunctionComponent<Props> = ({
  cells,
  active,
  editorNoteBlocks = [],
  introPhase = "done",
  onIntroStart,
  onIntroDone,
  onIntroSettled,
  onSlideLink,
  restoredScroll = 0,
  onNoteScroll,
}) => {
  const motionOn = useHubMotionOn();
  const motionOff = !motionOn;
  const narrow = useHubNarrowViewport();
  const rootRef = useRef<HTMLDivElement>(null);
  const dekRef = useRef<HTMLParagraphElement>(null);

  // Dek height depends on the copy. The lockup ends this far above the
  // stage bottom, then keeps the same gap Loudoun uses above its plate.
  useStageBottomClearance(rootRef, dekRef, "--title-dek-clear", narrow);
  /**
   * Keep clip machinery armed through `done` on narrow — flipping
   * `animateTitle` off for chapters and back on for `returning` re-attached
   * wipe attrs and flashed the note under the title close (credits-class).
   */
  const animateTitle = narrow && motionOn;
  /** Wipe: in-cell clones. Settled chapters: one hed above the rules. */
  const showWipeHed =
    animateTitle &&
    (introPhase === "hold" ||
      introPhase === "wiping" ||
      introPhase === "returning");
  const showSettledHed = narrow && introPhase === "done";

  // Stay true for the whole outgoing Loudoun wipe (title underlay).
  const [mediaLive, setMediaLive] = useState(() => active && motionOn);
  if (motionOff) {
    if (mediaLive) {
      setMediaLive(false);
    }
  } else if (active && !mediaLive) {
    setMediaLive(true);
  }
  useEffect(() => {
    if (motionOff) {
      setMediaLive(false);
      return;
    }
    if (active) {
      setMediaLive(true);
      return;
    }
    let timer = 0;
    let innerFrame = 0;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => {
        timer = window.setTimeout(
          () => setMediaLive(false),
          introWipeMs(narrow),
        );
      });
    });
    return () => {
      cancelAnimationFrame(outerFrame);
      cancelAnimationFrame(innerFrame);
      window.clearTimeout(timer);
    };
  }, [active, motionOff, narrow]);
  const mediaActive = mediaLive && motionOn;
  // Note covers are opaque — pause under hold only. `mediaLive` already spans
  // the outgoing wipe so loops don't poster-snap mid-transition.
  const playback = mediaActive && introPhase !== "hold";

  /**
   * Clip-path open state. Desktop / motion-off → always open.
   * Mobile: closed on hold, open on wiping/done, close on returning.
   */
  const [open, setOpen] = useState(
    () =>
      !(narrow && motionOn) ||
      introPhase === "done" ||
      introPhase === "returning",
  );
  /** Same enter wipe pins as chapters — see `data-hub-fill` / `fill-lock`. */
  const [fillPin, setFillPin] = useState(
    () => !(narrow && motionOn) || introPhase === "done",
  );
  const [fillLock, setFillLock] = useState(
    () => !(narrow && motionOn) || introPhase === "done",
  );
  useLayoutEffect(() => {
    if (!animateTitle) {
      setOpen(true);
      setFillPin(true);
      setFillLock(true);
      return;
    }
    if (introPhase === "hold") {
      setOpen(false);
      setFillPin(false);
      setFillLock(false);
      return;
    }
    if (introPhase === "wiping") {
      setOpen(false);
      setFillPin(false);
      setFillLock(false);
      return;
    }
    // Returning / done: keep the open fill-lock. Clearing it on the first
    // return frame re-armed close from a cold pin and flashed the note
    // (same handoff class as credits→Arizona).
    if (introPhase === "returning" || introPhase === "done") {
      setOpen(true);
      setFillPin(true);
      setFillLock(true);
      return;
    }
    setOpen(true);
    setFillPin(true);
    setFillLock(true);
  }, [introPhase, animateTitle]);
  useEffect(() => {
    if (!animateTitle) {
      return;
    }
    if (introPhase === "wiping") {
      let cancelFillPins: (() => void) | undefined;
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => {
          setOpen(true);
          cancelFillPins = scheduleEnterFillPins({
            wipeMs: introWipeMs(narrow),
            setFillPin,
            setFillLock,
          });
        });
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
        cancelFillPins?.();
      };
    }
    if (introPhase !== "returning") {
      return;
    }
    // Chapter-style depart: hold the open pin, then close.
    let openRaf = 0;
    const departDebounce = window.setTimeout(() => {
      setFillPin(false);
      setFillLock(false);
      openRaf = requestAnimationFrame(() => {
        openRaf = requestAnimationFrame(() => {
          setOpen(false);
        });
      });
    }, 120);
    return () => {
      window.clearTimeout(departDebounce);
      cancelAnimationFrame(openRaf);
    };
  }, [introPhase, animateTitle, narrow]);

  const clip = (fromClass: string) =>
    getBrowserInfo()?.android
      ? clsx(fromClass, open && styles.open)
      : clsx(open ? styles.open : fromClass);
  const titleClip = wipeClipAnimAttrs({
    animate: animateTitle,
    active: introPhase === "wiping",
    // Pin open while chapters are up so return doesn't re-arm wipe attrs.
    hold: introPhase === "done",
    depart: introPhase === "returning",
    open,
    fillPin,
    fillLock,
  });

  // Stay mounted through `done` so a return to the note keeps scroll + SVG
  // draw state (IntroCovers hides itself while chapters are up).
  const showIntro =
    !motionOff &&
    editorNoteBlocks.length > 0 &&
    Boolean(onIntroStart && onIntroDone && onIntroSettled);

  return (
    <div
      ref={rootRef}
      className={styles.root}
      {...titleClip}
      aria-label="Title slide"
      aria-hidden={active ? undefined : true}
    >
      {/* TR — desktop top-right / mobile top-left (left→right). */}
      <div className={styles.trShell}>
        <div className={clsx(styles.tr, clip(styles.fromLeft))}>
          <div className={styles.mediaFill}>
            <TitleCellMedia
              // Narrow: one loop (main). Secondary videos → posters.
              media={
                narrow ? asStill(titleSlideMedia.tr) : titleSlideMedia.tr
              }
              active={mediaActive}
              playback={playback}
            />
          </div>
          {showWipeHed ? (
            <HedBleed offsetClass={styles.hedBleedTr} active={active} />
          ) : null}
        </div>
      </div>

      {/* TL — desktop top-left split / mobile top-right (right→left). */}
      <div className={styles.tlShell}>
        <div className={clsx(styles.tl, clip(styles.fromRight))}>
          <div className={styles.splitStack}>
            <div className={styles.splitPane}>
              <TitleCellMedia
                media={titleSlideMedia.tlTop}
                active={mediaActive}
                playback={playback}
              />
            </div>
            <div className={styles.splitPane}>
              <TitleCellMedia
                media={titleSlideMedia.tlBottom}
                active={mediaActive}
                playback={playback}
              />
            </div>
          </div>
          {showWipeHed ? (
            <HedBleed offsetClass={styles.hedBleedTl} active={active} />
          ) : null}
        </div>
      </div>

      {/* TM — desktop top-middle / mobile bottom-right (bottom→top). */}
      <div className={styles.tmShell}>
        <div className={clsx(styles.tm, clip(styles.fromBottom))}>
          <div className={styles.mediaFill}>
            <TitleCellMedia
              media={titleSlideMedia.tm}
              active={mediaActive}
              playback={playback}
            />
          </div>
          {showWipeHed ? (
            <HedBleed offsetClass={styles.hedBleedTm} active={active} />
          ) : null}
        </div>
      </div>

      {/* Main — bl-merged (bottom→top). Both deks live inside so they share this reveal. */}
      <div className={styles.mainShell}>
        <div className={clsx(styles.main, clip(styles.fromBottom))}>
          <div className={styles.mediaFill}>
            <TitleCellMedia
              media={titleSlideMedia.blMerged}
              active={mediaActive}
              playback={playback}
            />
          </div>
          <p className={styles.dek}>{titleSlideMedia.dek}</p>
          <p ref={dekRef} className={styles.dekMobile}>
            {titleSlideMedia.dek}
          </p>
          {showWipeHed ? (
            <HedBleed offsetClass={styles.hedBleedMain} active={active} />
          ) : null}
        </div>
      </div>

      {/* BR — desktop bottom-right split / mobile bottom-left (top→bottom). */}
      <div className={styles.brShell}>
        <div className={clsx(styles.br, clip(styles.fromTop))}>
          <div className={styles.splitStack}>
            <div className={styles.splitPane}>
              <TitleCellMedia
                media={
                  narrow
                    ? asStill(titleSlideMedia.brTop)
                    : titleSlideMedia.brTop
                }
                active={mediaActive}
                playback={playback}
              />
            </div>
            <div className={styles.splitPane}>
              <TitleCellMedia
                media={titleSlideMedia.brBottom}
                active={mediaActive}
                playback={playback}
              />
            </div>
          </div>
          {showWipeHed ? (
            <HedBleed offsetClass={styles.hedBleedBr} active={active} />
          ) : null}
        </div>
      </div>

      {/* Desktop always; narrow only when not wiping — above cell rules. */}
      {!narrow || showSettledHed ? (
        <div className={styles.hedClip}>
          {narrow ? (
            <div className={styles.hedSettledMount}>
              <HedBleed active={active} />
            </div>
          ) : (
            <div className={clsx(styles.hed, clip(styles.fromCenter))}>
              <img
                className={styles.hedImg}
                src={titleSlideMedia.hed}
                alt=""
                aria-hidden="true"
                decoding="async"
                loading="eager"
                fetchPriority={active ? "high" : "low"}
              />
            </div>
          )}
        </div>
      ) : null}

      <h1 className="sr-only">The Center Will Not Hold</h1>

      {showIntro ? (
        <div className={styles.forelayer}>
          <IntroCovers
            // Desktop 3×2 tiling for the note — independent of the title
            // mosaic layout (covers stay put under the stage on narrow wipe).
            cells={cells}
            blocks={editorNoteBlocks}
            phase={introPhase}
            motionOff={motionOff}
            onStart={onIntroStart!}
            onDone={onIntroDone!}
            onSettled={onIntroSettled!}
            onSlideLink={onSlideLink}
            restoredScroll={restoredScroll}
            onNoteScroll={onNoteScroll}
          />
        </div>
      ) : null}
    </div>
  );
};

export default TitleSlide;
